import { useCallback, useEffect, useRef, useState } from "react";
import {
  JOB_TYPE_LABELS,
  type JobType,
  type Customer,
  type Property,
  type Driver,
  type Truck,
  type DumpsterSize,
  type Landfill,
  type NewCustomer,
  type NewProperty,
  type NewLandfill,
} from "@eagledispatch/shared";
import { apiFetch, ApiError, type SessionUser } from "../../lib/api";
import { idempotentCreate, invalidateOnEdit } from "../../lib/idempotent-create";
import Button from "../../components/Button";
import Card from "../../components/Card";
import FieldError from "../../components/FieldError";
import type { DispatcherRoute } from "./DispatchHome";

type DropdownState<T> =
  | { status: "loading" }
  | { status: "ok"; data: T[] }
  | { status: "error" };

const JOB_TYPES = Object.keys(JOB_TYPE_LABELS) as JobType[];

/**
 * A single dropdown that loads independently and can retry without blocking.
 */
function useDropdown<T>(load: () => Promise<T[]>): [DropdownState<T>, () => void] {
  const [state, setState] = useState<DropdownState<T>>({ status: "loading" });
  const loadRef = useRef(load);
  loadRef.current = load;

  const run = useCallback(() => {
    setState({ status: "loading" });
    loadRef.current()
      .then((data) => setState({ status: "ok", data }))
      .catch(() => setState({ status: "error" }));
  }, []);

  useEffect(() => {
    void run();
  }, [run]);
  return [state, run];
}

function option(value: string, label: string, disabled = false) {
  return (
    <option key={value} value={value} disabled={disabled}>
      {label}
    </option>
  );
}

export default function JobForm({
  currentUser,
  onNavigate,
  onCreated,
}: {
  currentUser?: SessionUser | null;
  onNavigate: (route: DispatcherRoute) => void;
  onCreated: (jobNumber: string) => void;
}) {
  // Form values.
  const [customerId, setCustomerId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [truckId, setTruckId] = useState("");
  const [sizeId, setSizeId] = useState("");
  const [jobType, setJobType] = useState<JobType | "">("");
  const [landfillId, setLandfillId] = useState("");
  const [message, setMessage] = useState("");

  const [properties, setProperties] = useState<DropdownState<Property>>({
    status: "loading",
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ── SPEC-48 Fix F: one idempotency key per logical submission ─────────────
  // Held in a ref so a retry-after-network-failure replays the SAME key and the
  // server returns its cached 201 instead of creating a duplicate job. Cleared
  // on success and on any field edit (an edit is a new intent) — see the
  // invariant effect below.
  const createKeyRef = useRef<string | null>(null);

  // In-form reference creation modal / state (Unit #20)
  // inlineMode: null | "new-customer" | "new-property" | "new-landfill"
  const [inlineMode, setInlineMode] = useState<
    "new-customer" | "new-property" | "new-landfill" | null
  >(null);
  const [inlineLoading, setInlineLoading] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [inlineFieldErrors, setInlineFieldErrors] = useState<
    Record<string, string>
  >({});

  // Inline New Customer + Property fields
  const [ncCompany, setNcCompany] = useState("");
  const [ncLine1, setNcLine1] = useState("");
  const [ncCity, setNcCity] = useState("Salinas");
  const [ncState] = useState("CA");
  const [ncZip, setNcZip] = useState("");
  const [ncContact, setNcContact] = useState("");
  const [ncPhone, setNcPhone] = useState("");
  // Follow-up property for new customer
  const [ncSiteName, setNcSiteName] = useState("");
  const [ncPropLine1, setNcPropLine1] = useState("");
  const [ncPropCity, setNcPropCity] = useState("Salinas");
  const [ncPropState] = useState("CA");
  const [ncPropZip, setNcPropZip] = useState("");

  // Inline New Location (Property only) fields
  const [npSiteName, setNpSiteName] = useState("");
  const [npLine1, setNpLine1] = useState("");
  const [npCity, setNpCity] = useState("Salinas");
  const [npState] = useState("CA");
  const [npZip, setNpZip] = useState("");
  const [npContact, setNpContact] = useState("");
  const [npPhone, setNpPhone] = useState("");

  // Inline New Landfill fields
  const [nlName, setNlName] = useState("");
  const [nlAddress, setNlAddress] = useState("");

  // Permissions from currentUser session (defaults to true if undefined)
  const canCreateCust = currentUser?.canCreateCustomers ?? true;
  const canCreateProp = currentUser?.canCreateProperties ?? true;
  const canCreateLand = currentUser?.canCreateLandfills ?? true;

  const [customers, reloadCustomers] = useDropdown<Customer>(() =>
    apiFetch<Customer[]>("/api/v1/customers"),
  );
  const [drivers, reloadDrivers] = useDropdown<Driver>(() =>
    apiFetch<Driver[]>("/api/v1/drivers?available=true"),
  );
  const [trucks, reloadTrucks] = useDropdown<Truck>(() =>
    apiFetch<Truck[]>("/api/v1/trucks"),
  );
  const [sizes, reloadSizes] = useDropdown<DumpsterSize>(() =>
    apiFetch<DumpsterSize[]>("/api/v1/dumpster-sizes"),
  );
  const [landfills, reloadLandfills] = useDropdown<Landfill>(() =>
    apiFetch<Landfill[]>("/api/v1/landfills"),
  );

  // ── Property cascade (customer → properties) ──────────────────────────────
  const propertiesAbortRef = useRef<AbortController | null>(null);
  const selectedCustomerRef = useRef(customerId);
  selectedCustomerRef.current = customerId;

  const loadProperties = useCallback((cid: string) => {
    propertiesAbortRef.current?.abort();
    const controller = new AbortController();
    propertiesAbortRef.current = controller;
    setProperties({ status: "loading" });
    apiFetch<Property[]>(`/api/v1/customers/${cid}/properties`, {
      signal: controller.signal,
    })
      .then((data) => {
        if (!controller.signal.aborted && selectedCustomerRef.current === cid) {
          setProperties({ status: "ok", data });
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        if (selectedCustomerRef.current !== cid) return;
        setProperties({ status: "error" });
      });
  }, []);

  // Cascade: property fetch on customer select.
  useEffect(() => {
    if (!customerId) {
      propertiesAbortRef.current?.abort();
      propertiesAbortRef.current = null;
      setProperties({ status: "loading" });
      setPropertyId("");
      return;
    }
    setPropertyId("");
    loadProperties(customerId);
    return () => propertiesAbortRef.current?.abort();
  }, [customerId, loadProperties]);

  // ── Inline Creation Handlers ──────────────────────────────────────────────

  const handleCreateInlineCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    setInlineFieldErrors({});

    const errs: Record<string, string> = {};
    if (!ncCompany.trim()) errs.companyName = "Company name is required";
    if (!ncLine1.trim()) errs.billingAddressLine1 = "Billing address is required";
    if (!ncCity.trim()) errs.billingCity = "Billing city is required";
    if (!ncZip.trim()) errs.billingZip = "Billing zip is required";

    // Immediate property requirement
    if (!ncSiteName.trim()) errs.siteName = "Site name is required";
    if (!ncPropLine1.trim()) errs.addressLine1 = "Site address is required";
    if (!ncPropCity.trim()) errs.city = "Site city is required";
    if (!ncPropZip.trim()) errs.zip = "Site zip is required";

    if (Object.keys(errs).length > 0) {
      setInlineFieldErrors(errs);
      return;
    }

    setInlineLoading(true);
    let createdCustId: string | null = null;
    try {
      // 1. Create Customer
      const custPayload: NewCustomer = {
        companyName: ncCompany.trim(),
        billingAddressLine1: ncLine1.trim(),
        billingCity: ncCity.trim(),
        billingState: ncState.trim(),
        billingZip: ncZip.trim(),
        contactName: ncContact.trim() || null,
        contactPhone: ncPhone.trim() || null,
      };
      const createdCust = await apiFetch<Customer>("/api/v1/customers", {
        method: "POST",
        body: JSON.stringify(custPayload),
      });
      createdCustId = createdCust.id;

      // 2. Create Initial Property
      const propPayload: NewProperty = {
        customerId: createdCust.id,
        siteName: ncSiteName.trim(),
        addressLine1: ncPropLine1.trim(),
        city: ncPropCity.trim(),
        state: ncPropState.trim(),
        zip: ncPropZip.trim(),
        contactName: ncContact.trim() || null,
        contactPhone: ncPhone.trim() || null,
      };
      const createdProp = await apiFetch<Property>(
        `/api/v1/customers/${createdCust.id}/properties`,
        {
          method: "POST",
          body: JSON.stringify(propPayload),
        },
      );

      // 3. Reload dropdowns and select new pair
      reloadCustomers();
      setCustomerId(createdCust.id);
      setPropertyId(createdProp.id);
      setProperties({ status: "ok", data: [createdProp] });

      setInlineMode(null);
    } catch (err: unknown) {
      if (createdCustId !== null) {
        // Recovery: customer was created (201), but property creation failed.
        // Reload customers list, select the created customer, populate property fields,
        // and switch to "new-property" mode so a retry posts ONLY the property without
        // duplicate-creating the customer or leaving an unusable orphan.
        reloadCustomers();
        setCustomerId(createdCustId);
        setNpSiteName(ncSiteName);
        setNpLine1(ncPropLine1);
        setNpCity(ncPropCity);
        setNpZip(ncPropZip);
        setNpContact(ncContact);
        setNpPhone(ncPhone);
        setInlineMode("new-property");
      }
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setInlineFieldErrors(err.fieldErrors);
        } else {
          setInlineError(err.message);
        }
      } else {
        setInlineError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setInlineLoading(false);
    }
  };

  const handleCreateInlineProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) return;
    setInlineError(null);
    setInlineFieldErrors({});

    const errs: Record<string, string> = {};
    if (!npSiteName.trim()) errs.siteName = "Site name is required";
    if (!npLine1.trim()) errs.addressLine1 = "Site address is required";
    if (!npCity.trim()) errs.city = "Site city is required";
    if (!npZip.trim()) errs.zip = "Site zip is required";

    if (Object.keys(errs).length > 0) {
      setInlineFieldErrors(errs);
      return;
    }

    setInlineLoading(true);
    try {
      const propPayload: NewProperty = {
        customerId,
        siteName: npSiteName.trim(),
        addressLine1: npLine1.trim(),
        city: npCity.trim(),
        state: npState.trim(),
        zip: npZip.trim(),
        contactName: npContact.trim() || null,
        contactPhone: npPhone.trim() || null,
      };
      const createdProp = await apiFetch<Property>(
        `/api/v1/customers/${customerId}/properties`,
        {
          method: "POST",
          body: JSON.stringify(propPayload),
        },
      );

      // Reload properties for customer & select it
      loadProperties(customerId);
      setPropertyId(createdProp.id);
      setInlineMode(null);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setInlineFieldErrors(err.fieldErrors);
        } else {
          setInlineError(err.message);
        }
      } else {
        setInlineError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setInlineLoading(false);
    }
  };

  const handleCreateInlineLandfill = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    setInlineFieldErrors({});

    if (!nlName.trim()) {
      setInlineFieldErrors({ name: "Landfill name is required" });
      return;
    }

    setInlineLoading(true);
    try {
      const landfillPayload: NewLandfill = {
        name: nlName.trim(),
        address: nlAddress.trim() || null,
      };
      const created = await apiFetch<Landfill>("/api/v1/landfills", {
        method: "POST",
        body: JSON.stringify(landfillPayload),
      });

      reloadLandfills();
      setLandfillId(created.id);
      setInlineMode(null);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (Object.keys(err.fieldErrors).length > 0) {
          setInlineFieldErrors(err.fieldErrors);
        } else {
          setInlineError(err.message);
        }
      } else {
        setInlineError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setInlineLoading(false);
    }
  };

  // ── Main Job Submit ───────────────────────────────────────────────────────

  // DO-LANDFILL-OPTIONAL: a D/O (delivery-only) job has NO landfill — the
  // field is hidden below and excluded from the payload. The server
  // independently enforces the same rule (shared NewJob.superRefine rejects
  // a D/O body carrying a landfillId).
  const isDeliveryOnly = jobType === "D_O";

  const requiredReady =
    !!customerId &&
    !!propertyId &&
    !!driverId &&
    !!truckId &&
    !!sizeId &&
    !!jobType &&
    // DO-LANDFILL-OPTIONAL: D/O needs no landfill; other types still do.
    (isDeliveryOnly || !!landfillId);

  // ── SPEC-48 Fix F invariant: an edited payload is a NEW intent ───────────
  // Declared as one effect over the submit-relevant values instead of a
  // `invalidateOnEdit()` call threaded through 8 onChange handlers — the
  // handlers version fails silently the day someone adds a ninth field. Any
  // change to what would be sent clears the held key, so a retry after an edit
  // mints a fresh one rather than replaying a cached 201 for different data.
  // (The ref write is idempotent, so this running on mount / cascade resets is
  // harmless.)
  useEffect(() => {
    invalidateOnEdit(createKeyRef);
  }, [customerId, propertyId, driverId, truckId, sizeId, jobType, landfillId, message]);

  const submit = async () => {
    if (!requiredReady || !jobType) return;
    setSubmitting(true);
    setSubmitError(null);
    setFieldErrors({});
    // The form is frozen while `submitting` (fields readOnly, button
    // disabled), so the payload below cannot drift from the key being sent.
    const result = await idempotentCreate<{ jobNumber: string }>("/api/v1/jobs", {
      customerId,
      propertyId,
      driverId,
      truckId,
      dumpsterSizeId: sizeId,
      jobType,
      // DO-LANDFILL-OPTIONAL: D/O carries no landfill — the key is fully
      // ABSENT on the wire (spread, not a conditional value), matching the
      // server's strict D/O reject-a-sent-landfill rule.
      ...(isDeliveryOnly ? {} : { landfillId }),
      message: message.trim() === "" ? undefined : message,
    }, createKeyRef);
    setSubmitting(false);

    if (result.ok) {
      onCreated(result.data.jobNumber);
      return;
    }
    const e = result.error;
    if (e instanceof ApiError) {
      if (Object.keys(e.fieldErrors).length > 0) {
        setFieldErrors(e.fieldErrors);
      } else {
        setSubmitError(e.message);
      }
    } else {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <main className="surface">
      <header className="surface-header">
        <Button variant="ghost" onClick={() => onNavigate({ name: "home" })}>
          ← Back
        </Button>
        <h1>New Job</h1>
      </header>

      <Card>
        <form
          className="job-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {/* Customer Dropdown + New Customer Option */}
          <label className="field">
            <span className="field-label">Customer</span>
            <select
              value={customerId}
              disabled={submitting}
              onChange={(e) => {
                if (e.target.value === "__NEW_CUSTOMER__") {
                  setInlineMode("new-customer");
                  setInlineError(null);
                  setInlineFieldErrors({});
                  setNcCompany("");
                  setNcLine1("");
                  setNcCity("Salinas");
                  setNcZip("");
                  setNcContact("");
                  setNcPhone("");
                  setNcSiteName("");
                  setNcPropLine1("");
                  setNcPropCity("Salinas");
                  setNcPropZip("");
                } else {
                  setCustomerId(e.target.value);
                }
              }}
            >
              <option value="">Select customer…</option>
              {canCreateCust && (
                <option value="__NEW_CUSTOMER__">+ New customer…</option>
              )}
              {customers.status === "ok" &&
                customers.data.map((c) => option(c.id, c.companyName))}
            </select>
            {customers.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load customers.</span>{" "}
                <button type="button" className="link-button" onClick={reloadCustomers}>
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.customerId} />
          </label>

          {/* Property Dropdown + New Location Option */}
          <label className="field">
            <span className="field-label">Job Site</span>
            <select
              value={propertyId}
              disabled={submitting || !customerId || properties.status !== "ok"}
              onChange={(e) => {
                if (e.target.value === "__NEW_PROPERTY__") {
                  setInlineMode("new-property");
                  setInlineError(null);
                  setInlineFieldErrors({});
                  setNpSiteName("");
                  setNpLine1("");
                  setNpCity("Salinas");
                  setNpZip("");
                  setNpContact("");
                  setNpPhone("");
                } else {
                  setPropertyId(e.target.value);
                }
              }}
            >
              <option value="">
                {customerId ? "Select property…" : "Choose a customer first"}
              </option>
              {customerId && canCreateProp && (
                <option value="__NEW_PROPERTY__">+ New location…</option>
              )}
              {properties.status === "ok" &&
                properties.data.map((p) =>
                  option(p.id, `${p.siteName} — ${p.addressLine1}`),
                )}
            </select>
            {!customerId && <span className="muted">Pick a customer to load properties.</span>}
            {properties.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load properties.</span>{" "}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => loadProperties(customerId)}
                >
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.propertyId} />
          </label>

          <label className="field">
            <span className="field-label">Driver</span>
            <select
              value={driverId}
              disabled={submitting}
              onChange={(e) => setDriverId(e.target.value)}
            >
              <option value="">Select driver…</option>
              {drivers.status === "ok" &&
                drivers.data.map((d) => option(d.id, d.name))}
            </select>
            {drivers.status === "ok" && drivers.data.length === 0 && (
              <span className="muted">No drivers available.</span>
            )}
            {drivers.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load drivers.</span>{" "}
                <button type="button" className="link-button" onClick={reloadDrivers}>
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.driverId} />
          </label>

          <label className="field">
            <span className="field-label">Truck</span>
            <select
              value={truckId}
              disabled={submitting}
              onChange={(e) => setTruckId(e.target.value)}
            >
              <option value="">Select truck…</option>
              {trucks.status === "ok" &&
                trucks.data.map((t) => option(t.id, `Truck ${t.unitNumber}`))}
            </select>
            {trucks.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load trucks.</span>{" "}
                <button type="button" className="link-button" onClick={reloadTrucks}>
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.truckId} />
          </label>

          <label className="field">
            <span className="field-label">Dumpster Size</span>
            <select
              value={sizeId}
              disabled={submitting}
              onChange={(e) => setSizeId(e.target.value)}
            >
              <option value="">Select size…</option>
              {sizes.status === "ok" &&
                sizes.data.map((s) => option(s.id, `${s.label} (${s.yards} yd)`))}
            </select>
            {sizes.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load sizes.</span>{" "}
                <button type="button" className="link-button" onClick={reloadSizes}>
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.dumpsterSizeId} />
          </label>

          <label className="field">
            <span className="field-label">Job Type</span>
            <select
              value={jobType}
              disabled={submitting}
              onChange={(e) => setJobType(e.target.value as JobType | "")}
            >
              <option value="">Select type…</option>
              {JOB_TYPES.map((t) => option(t, JOB_TYPE_LABELS[t]))}
            </select>
            <FieldError message={fieldErrors.jobType} />
          </label>

          {/* DO-LANDFILL-OPTIONAL: hidden for D/O — delivery-only means the
              job never dumps a load, so there is no landfill to pick. If the
              dispatcher switches the type AWAY from D/O, the dropdown
              re-appears and the gate re-demands a landfill. */}
          {isDeliveryOnly && (
            <p className="muted" style={{ fontSize: "13px" }}>
              Delivery-only — no landfill needed.
            </p>
          )}
          {!isDeliveryOnly && (
          <label className="field">
            <span className="field-label">Landfill</span>
            <select
              value={landfillId}
              disabled={submitting}
              onChange={(e) => {
                if (e.target.value === "__NEW_LANDFILL__") {
                  setInlineMode("new-landfill");
                  setInlineError(null);
                  setInlineFieldErrors({});
                  setNlName("");
                  setNlAddress("");
                } else {
                  setLandfillId(e.target.value);
                }
              }}
            >
              <option value="">Select landfill…</option>
              {canCreateLand && (
                <option value="__NEW_LANDFILL__">+ New landfill…</option>
              )}
              {landfills.status === "ok" &&
                landfills.data.map((l) => option(l.id, l.name))}
            </select>
            {landfills.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load landfills.</span>{" "}
                <button type="button" className="link-button" onClick={reloadLandfills}>
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.landfillId} />
          </label>
          )}

          <label className="field">
            <span className="field-label">Message (optional)</span>
            <textarea
              value={message}
              readOnly={submitting}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Gate codes, contact name/phone, instructions…"
              rows={3}
            />
          </label>

          {submitError && <p className="form-error err">{submitError}</p>}

          <Button
            type="submit"
            variant="primary"
            className="send-button"
            disabled={!requiredReady || submitting}
          >
            {submitting ? "Sending…" : "Send to Driver"}
          </Button>
        </form>
      </Card>

      {/* ── INLINE MODALS / FORMS ── */}

      {/* Modal 1: New Customer + Initial Property */}
      {inlineMode === "new-customer" && (
        <div className="modal-overlay" onClick={() => setInlineMode(null)}>
          <div
            className="modal-content"
            style={{ maxWidth: "540px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Create New Customer</h3>
            <p className="muted" style={{ fontSize: "13px", marginBottom: "12px" }}>
              Enter customer billing details and their primary job site location.
            </p>

            {inlineError && <p className="form-error err">{inlineError}</p>}

            <form onSubmit={handleCreateInlineCustomer}>
              <div className="form-group">
                <label>Company Name*</label>
                <input
                  value={ncCompany}
                  onChange={(e) => setNcCompany(e.target.value)}
                  placeholder="e.g. Salinas Valley Produce"
                  required
                />
                <FieldError message={inlineFieldErrors.companyName} />
              </div>

              <div className="form-group">
                <label>Billing Address Line 1*</label>
                <input
                  value={ncLine1}
                  onChange={(e) => setNcLine1(e.target.value)}
                  placeholder="e.g. 100 Main St"
                  required
                />
                <FieldError message={inlineFieldErrors.billingAddressLine1} />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>City*</label>
                  <input
                    value={ncCity}
                    onChange={(e) => setNcCity(e.target.value)}
                    placeholder="Salinas"
                    required
                  />
                  <FieldError message={inlineFieldErrors.billingCity} />
                </div>
                <div className="form-group">
                  <label>Zip*</label>
                  <input
                    value={ncZip}
                    onChange={(e) => setNcZip(e.target.value)}
                    placeholder="93901"
                    required
                  />
                  <FieldError message={inlineFieldErrors.billingZip} />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    value={ncContact}
                    onChange={(e) => setNcContact(e.target.value)}
                    placeholder="e.g. John Doe"
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    value={ncPhone}
                    onChange={(e) => setNcPhone(e.target.value)}
                    placeholder="e.g. 831-555-0188"
                  />
                </div>
              </div>

              <hr style={{ margin: "16px 0", borderColor: "#eee" }} />
              <h4>Initial Job Site Location</h4>

              <div className="form-group">
                <label>Site Name*</label>
                <input
                  value={ncSiteName}
                  onChange={(e) => setNcSiteName(e.target.value)}
                  placeholder="e.g. Main Yard / HQ"
                  required
                />
                <FieldError message={inlineFieldErrors.siteName} />
              </div>

              <div className="form-group">
                <label>Site Address Line 1*</label>
                <input
                  value={ncPropLine1}
                  onChange={(e) => setNcPropLine1(e.target.value)}
                  placeholder="e.g. 100 Main St"
                  required
                />
                <FieldError message={inlineFieldErrors.addressLine1} />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Site City*</label>
                  <input
                    value={ncPropCity}
                    onChange={(e) => setNcPropCity(e.target.value)}
                    placeholder="Salinas"
                    required
                  />
                  <FieldError message={inlineFieldErrors.city} />
                </div>
                <div className="form-group">
                  <label>Site Zip*</label>
                  <input
                    value={ncPropZip}
                    onChange={(e) => setNcPropZip(e.target.value)}
                    placeholder="93901"
                    required
                  />
                  <FieldError message={inlineFieldErrors.zip} />
                </div>
              </div>

              <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                <Button type="submit" variant="primary" disabled={inlineLoading}>
                  {inlineLoading ? "Creating…" : "Save Customer & Location"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setInlineMode(null)}
                  disabled={inlineLoading}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: New Property for Existing Customer */}
      {inlineMode === "new-property" && (
        <div className="modal-overlay" onClick={() => setInlineMode(null)}>
          <div
            className="modal-content"
            style={{ maxWidth: "480px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Add New Job Site Location</h3>
            <p className="muted" style={{ fontSize: "13px", marginBottom: "12px" }}>
              Add a new property site for the selected customer.
            </p>

            {inlineError && <p className="form-error err">{inlineError}</p>}

            <form onSubmit={handleCreateInlineProperty}>
              <div className="form-group">
                <label>Site Name*</label>
                <input
                  value={npSiteName}
                  onChange={(e) => setNpSiteName(e.target.value)}
                  placeholder="e.g. Processing Facility #2"
                  required
                />
                <FieldError message={inlineFieldErrors.siteName} />
              </div>

              <div className="form-group">
                <label>Site Address Line 1*</label>
                <input
                  value={npLine1}
                  onChange={(e) => setNpLine1(e.target.value)}
                  placeholder="e.g. 450 Agriculture Way"
                  required
                />
                <FieldError message={inlineFieldErrors.addressLine1} />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>City*</label>
                  <input
                    value={npCity}
                    onChange={(e) => setNpCity(e.target.value)}
                    placeholder="Salinas"
                    required
                  />
                  <FieldError message={inlineFieldErrors.city} />
                </div>
                <div className="form-group">
                  <label>Zip*</label>
                  <input
                    value={npZip}
                    onChange={(e) => setNpZip(e.target.value)}
                    placeholder="93901"
                    required
                  />
                  <FieldError message={inlineFieldErrors.zip} />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    value={npContact}
                    onChange={(e) => setNpContact(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    value={npPhone}
                    onChange={(e) => setNpPhone(e.target.value)}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                <Button type="submit" variant="primary" disabled={inlineLoading}>
                  {inlineLoading ? "Adding…" : "Save Location"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setInlineMode(null)}
                  disabled={inlineLoading}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 3: New Landfill */}
      {inlineMode === "new-landfill" && (
        <div className="modal-overlay" onClick={() => setInlineMode(null)}>
          <div
            className="modal-content"
            style={{ maxWidth: "480px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Add New Landfill / Transfer Station</h3>

            {inlineError && <p className="form-error err">{inlineError}</p>}

            <form onSubmit={handleCreateInlineLandfill}>
              <div className="form-group">
                <label>Name*</label>
                <input
                  value={nlName}
                  onChange={(e) => setNlName(e.target.value)}
                  placeholder="e.g. Monterey Regional Waste"
                  required
                />
                <FieldError message={inlineFieldErrors.name} />
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  value={nlAddress}
                  onChange={(e) => setNlAddress(e.target.value)}
                  placeholder="e.g. 14201 Del Monte Blvd, Marina, CA"
                />
              </div>

              <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                <Button type="submit" variant="primary" disabled={inlineLoading}>
                  {inlineLoading ? "Adding…" : "Save Landfill"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setInlineMode(null)}
                  disabled={inlineLoading}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}
